import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { json } from '@remix-run/node'
import { useLoaderData, type MetaFunction } from '@remix-run/react'
import { serverOnly$ } from 'vite-env-only'
import { TableOfContents } from '#app/components/commonElements/TableOfContents.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import Avatar from '#app/components/pageLayout/Avatar.tsx'
import PageTitle from '#app/components/pageLayout/PageTitle.tsx'
import { Breadcrumb } from '#app/components/tramite/breadcrumb.tsx'
import {
	getAllPosts,
	getPost,
} from '#app/routes/_appAdmin+/posts/_postQueries.server.ts'
import { getPostImgSrc, invariantResponse } from '#app/utils/misc.tsx'

export async function loader({ params }: DataFunctionArgs) {
	const post = await getPost(String(params.post))

	invariantResponse(post, 'Esta guía no existe en Papelea', { status: 404 })
	//console.log(post)
	return json({
		post,
	})
}

export default function PostRoute() {
	const data = useLoaderData<typeof loader>()
	const { post } = data

	const pages = [
		{ name: 'Guías', href: '.././guias', current: false },
		{
			name: post.name,
			href: '#',
			current: true,
		},
	]
	/* const [updatedContent, setUpdatedContent] = useState(post.content)

	useEffect(() => {
		const updatedHtmlContent = insertAdsBeforeH2(post.content)
		setUpdatedContent(updatedHtmlContent)
	}, [post.content])

	useEffect(() => {
		const initializeEzoic = async () => {
			// Ensure ezstandalone is available (loaded asynchronously)
			if (window.ezstandalone) {
				window.ezstandalone.define(622)

				if (!window.ezstandalone.enabled) {
					window.ezstandalone.enable()
					window.ezstandalone.display()
				} else {
					window.ezstandalone.refresh()
				}
			} else {
				// Delay and try again if ezstandalone is not available yet
				setTimeout(initializeEzoic, 1000)
			}
		}

		initializeEzoic()
	}, []) */
	return (
		<>
			<main className="relative py-6 lg:grid lg:grid-cols-[1fr_300px] lg:gap-10 lg:py-10 xl:gap-20">
				<div>
					<Breadcrumb pages={pages} />
					<PageTitle
						title={post.name}
						sizeClasses="text-3xl sm:text-2xl lg:text-3xl"
					/>
					<Avatar user={post.owner} />

					<div className="prose mt-8 hidden border border-gray-100 md:block">
						<img
							className="w-full"
							src={getPostImgSrc(post.images[0]?.id)}
							alt={post.images[0]?.altText ?? ''}
							width="220"
							height="133"
							loading="lazy"
						/>
					</div>
					<div
						className="prose mt-6 max-w-prose  prose-a:font-semibold prose-a:text-amber-700 prose-a:no-underline hover:prose-a:text-amber-400"
						dangerouslySetInnerHTML={{ __html: post.content }}
					/>

					{/* {post.questions.length > 0 && (
						<>
							<hr className="mt-8" />
							<div className="prose my-8 ">
								<h2 id="preguntas-frecuentes">Preguntas frecuentes</h2>
							</div>
							{post.questions.map(question => (
								<div>{question.name}</div>
							))}
						</>
					)} */}
				</div>
				<div className="hidden text-sm lg:block">
					<div className="sticky top-16 -mt-10 max-h-[calc(var(--vh)-4rem)] overflow-y-auto pt-10">
						<TableOfContents
							headings={post.toc ? JSON.parse(post.toc) : []}
							otherSections={[]}
						/>
					</div>
				</div>
			</main>
		</>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const postName = data?.post.name ?? ''
	const postSummary =
		data && data.post.content.length > 100
			? data?.post.content.slice(0, 97) + '...'
			: 'No hay contenido'
	const metaTitle = `${postName} | Papelea `
	return [
		{ title: metaTitle },
		{
			tagName: 'link',
			rel: 'canonical',
			href: `https://papelea.com/${params.country}/guias/${postName}`,
		},
		{
			name: 'description',
			content: postSummary,
		},

		{
			property: 'robots',
			content: 'index, follow',
		},
		{
			property: 'og:type',
			content: 'website',
		},
		{
			property: 'og:url',
			href: `https://papelea.com/${params.country}/guias/${postName}`,
		},
		{
			property: 'og:title',
			content: metaTitle,
		},
		{
			name: 'og:description',
			content: postSummary,
		},
	]
}

export const handle: SEOHandle = serverOnly$({
	getSitemapEntries: async request => {
		const posts = await getAllPosts()
		return posts.map(post => {
			return {
				route: `/${post.country.code}/guias/${post.slug}`,
				priority: 0.7,
			}
		})
	},
})

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
